import styled from '@emotion/styled';
import React, { memo, useCallback, useRef, useState } from 'react';

import ContentBox from '~/components/ContentBox';
import { css } from '~/css';
import { useScreenValue } from '~/shared/hooks/screens';
import { skin } from '~/theme';

import SectionBase, { SectionBaseProps } from '../base';
import MobileCarousel from './Carousel.mobile';
import PcCarousel from './Carousel.pc';
import { CarouselCustomItem, CarouselItem, CarouselMode, CarouselPlatformVariant } from './types';
import useCarousel from './useCarousel';
import useSwipeEvent from './useSwipeEvent';

export interface CarouselSectionProps extends SectionBaseProps {
  className?: string;
  customItems?: CarouselCustomItem[];
  mode: CarouselMode;
  variant?: CarouselPlatformVariant;
  items: CarouselItem[];
}

const Container = styled(SectionBase)(
  skin({
    blackSquare: css({
      background: 'bg1',
    }),
  }),
);

const CarouselSection: React.FC<CarouselSectionProps> = ({
  id,
  title,
  description,
  mode,
  items,
  customItems,
  variant,
}) => {
  const isPc = useScreenValue({ mobile: false, pc: true });

  const [inTransition, setInTransition] = useState(false);
  const notInTransition = !inTransition || undefined;
  const { carouselItems, currentSlideIndex, setCurrentSlideIndex, goPrev, goNext } = useCarousel(
    mode,
    customItems,
    () => {
      setInTransition(true);
    },
  );

  const handleTransitionEnd = useCallback(() => {
    setInTransition(false);
  }, []);

  const swiperableRef = useRef<HTMLDivElement>(null);
  useSwipeEvent(
    swiperableRef,
    (direction) => (direction === 'right' ? goPrev() : direction === 'left' ? goNext() : null),
    50,
  );

  return (
    <Container id={id} title={title} description={description}>
      <ContentBox>
        {isPc ? (
          <PcCarousel
            key='carousel'
            mode={mode}
            items={carouselItems}
            currentIndex={currentSlideIndex}
            setCurrentIndex={notInTransition && setCurrentSlideIndex}
            goPrev={notInTransition && goPrev}
            goNext={notInTransition && goNext}
            onTransitionEnd={handleTransitionEnd}
          />
        ) : (
          <MobileCarousel
            key='carousel'
            ref={swiperableRef}
            mode={mode}
            items={carouselItems}
            currentIndex={currentSlideIndex}
            setCurrentIndex={notInTransition && setCurrentSlideIndex}
            goPrev={notInTransition && goPrev}
            goNext={notInTransition && goNext}
            onTransitionEnd={handleTransitionEnd}
            textSize={variant && variant.mobile && variant.mobile.dots && variant.mobile.dots.textSize}
          />
        )}
      </ContentBox>
    </Container>
  );
};

export default memo(CarouselSection);
